import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Copy, Share2, MessageSquare, Calendar, MapPin, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * FlyerLandingPage
 * ----------------
 * Public, unauth'd shareable page — this is the URL organizers blast to
 * approved vendors AND the URL vendors then paste into their social
 * captions. Content:
 *   • Flyer image (dominant)
 *   • Event title / date / location
 *   • One-tap share row: native OS share sheet, Copy caption, Download
 *     image, Twitter/FB/WhatsApp deep links.
 *   • CTA to see the full event page + apply.
 *
 * Uses the public `/events/{id}/flyer-view` endpoint so no auth required.
 */
export default function FlyerLandingPage() {
  const { eventId } = useParams();
  const [flyer, setFlyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/events/${eventId}/flyer-view`)
      .then(({ data }) => setFlyer(data))
      .catch(() => setError("This flyer isn't available yet."))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    if (!flyer) return;
    document.title = `${flyer.title} · AddVendTour flyer`;
    const meta = (attr, key, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    const url = window.location.href;
    const desc = flyer.share_caption || `${flyer.title} in ${flyer.city}`;
    meta("property", "og:title", `${flyer.title} · ${flyer.city}`);
    meta("property", "og:description", desc);
    meta("property", "og:image", flyer.flyer_url);
    meta("property", "og:url", url);
    meta("name", "twitter:card", "summary_large_image");
    meta("name", "twitter:image", flyer.flyer_url);
  }, [flyer]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const caption = flyer?.share_caption || "";

  const nativeShare = async () => {
    if (!navigator.share) {
      toast.error("Your browser doesn't support the share sheet — use the buttons below.");
      return;
    }
    try {
      await navigator.share({
        title: flyer.title,
        text: caption,
        url: shareUrl,
      });
    } catch (err) {
      // user canceled or the share sheet closed — silent
    }
  };

  const copyCaption = async () => {
    const text = caption + (shareUrl ? `\n\n${shareUrl}` : "");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Caption + link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the text to copy manually.");
    }
  };

  const downloadFlyer = async () => {
    if (!flyer?.flyer_url) return;
    setDownloading(true);
    try {
      const res = await fetch(flyer.flyer_url, { credentials: "omit" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `addvendtour-${flyer.title.replace(/\s+/g, "-").toLowerCase()}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Saved to your device.");
    } catch {
      toast.error("Couldn't download — try long-pressing the image.");
    } finally {
      setDownloading(false);
    }
  };

  const openTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener");
  };
  const openFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener");
  };
  const openWhatsapp = () => {
    const text = `${caption}\n\n${shareUrl}`;
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };

  if (loading) {
    return <div className="p-20 text-center font-serif text-2xl">Loading…</div>;
  }
  if (error || !flyer) {
    return (
      <div className="p-20 text-center">
        <div className="font-serif text-4xl">{error || "Not found"}</div>
        <Link to="/events" className="mt-6 inline-block btn-primary px-6 py-3 rounded-full">Browse events</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-8 sm:py-12" data-testid="flyer-landing-page">
      <Link
        to={`/events/${eventId}`}
        className="text-sm inline-flex items-center gap-1 mb-6"
        style={{ color: "var(--ink-soft)" }}
        data-testid="flyer-landing-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Event details
      </Link>

      {/* --- Flyer image (dominant) --- */}
      <div
        className="rounded-sm overflow-hidden shadow-lg mx-auto"
        style={{ maxWidth: 520, backgroundColor: "var(--sand)" }}
      >
        <img
          src={flyer.flyer_url}
          alt={`${flyer.title} flyer`}
          data-testid="flyer-landing-image"
          className="w-full h-auto block"
        />
      </div>

      {/* --- Event summary card --- */}
      <div className="card-warm rounded-sm p-5 sm:p-6 mt-5" data-testid="flyer-landing-summary">
        <div className="overline" style={{ color: "var(--terracotta)" }}>Come find us at</div>
        <h1 className="font-serif text-3xl sm:text-4xl mt-1 leading-tight" data-testid="flyer-landing-title">
          {flyer.title}
        </h1>
        <div className="mt-3 space-y-2 text-sm" style={{ color: "var(--ink)" }}>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 shrink-0" style={{ color: "var(--ink-soft)" }} />
            <span>{formatDate(flyer.date, flyer.event_time)}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0" style={{ color: "var(--ink-soft)" }} />
            <span>{[flyer.location, flyer.city].filter(Boolean).join(" · ")}</span>
          </div>
        </div>
        {caption && (
          <p
            className="mt-4 text-[15px] leading-relaxed"
            style={{ color: "var(--ink)" }}
            data-testid="flyer-landing-caption"
          >
            {caption}
          </p>
        )}
      </div>

      {/* --- Share actions --- */}
      <div className="card-warm rounded-sm p-5 sm:p-6 mt-4" data-testid="flyer-landing-share">
        <div className="overline mb-3" style={{ color: "var(--moss)" }}>Share this flyer</div>
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            data-testid="flyer-share-native"
            onClick={nativeShare}
            className="btn-primary rounded-full py-5 col-span-2 sm:col-span-1"
          >
            <Share2 className="h-4 w-4 mr-1.5" /> Share…
          </Button>
          <Button
            type="button"
            data-testid="flyer-share-copy"
            onClick={copyCaption}
            className="btn-outline rounded-full py-5"
          >
            {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
            {copied ? "Copied!" : "Copy caption"}
          </Button>
          <Button
            type="button"
            data-testid="flyer-share-download"
            onClick={downloadFlyer}
            disabled={downloading}
            variant="outline"
            className="rounded-full py-4"
          >
            <Download className="h-4 w-4 mr-1.5" /> Save image
          </Button>
          <Button
            type="button"
            data-testid="flyer-share-twitter"
            onClick={openTwitter}
            variant="outline"
            className="rounded-full py-4"
          >
            X / Twitter
          </Button>
          <Button
            type="button"
            data-testid="flyer-share-facebook"
            onClick={openFacebook}
            variant="outline"
            className="rounded-full py-4"
          >
            Facebook
          </Button>
          <Button
            type="button"
            data-testid="flyer-share-whatsapp"
            onClick={openWhatsapp}
            variant="outline"
            className="rounded-full py-4"
          >
            WhatsApp
          </Button>
        </div>
        <p className="mt-3 text-[11px]" style={{ color: "var(--ink-soft)" }}>
          Long-press the flyer above to save it directly on mobile.
        </p>
      </div>

      {/* --- CTA back to event --- */}
      <div className="mt-4 text-center">
        <Link
          to={`/events/${eventId}`}
          data-testid="flyer-landing-event-link"
          className="btn-outline rounded-full inline-flex items-center gap-1.5 px-6 py-3 text-sm"
        >
          <MessageSquare className="h-4 w-4" /> See the event details
        </Link>
      </div>
    </div>
  );
}

function formatDate(iso, time) {
  if (!iso) return "";
  try {
    const d = new Date(iso + (time ? `T${time}:00` : "T00:00:00"));
    const base = d.toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
    return time ? `${base} · ${time}` : base;
  } catch {
    return iso;
  }
}
